import React, { type ComponentType, useCallback, memo } from 'react';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { RenderOnClient } from '@atlassian/jira-ssr-toolkit/src/render-on-client.tsx';
import {
	GLOBAL_COMPONENTS_MARK,
	GLOBAL_COMPONENTS_START_MARK,
	GLOBAL_COMPONENTS_END_MARK,
} from '@atlassian/jira-page-container-constants/src/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { renderGlobalComponents } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';

export type GlobalComponentsProps = {
	sharedGlobalComponents: ReadonlyArray<ComponentType<{}>>;
};

const routeComponents: Array<never> = [];

const RouteGlobalComponents = () => {
	const route = useCurrentRoute();

	return <>{renderGlobalComponents(route.layout?.globalComponents || routeComponents)}</>;
};

// This component is necessary to ensure that the error boundary picks up errors in the renderGlobalComponents function
const SharedGlobalComponents = ({ sharedGlobalComponents }: GlobalComponentsProps) => (
	<>{renderGlobalComponents(sharedGlobalComponents)}</>
);

export const GlobalComponents = memo(({ sharedGlobalComponents }: GlobalComponentsProps) => {
	const globalComponentsPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			GLOBAL_COMPONENTS_MARK,
			GLOBAL_COMPONENTS_START_MARK,
			GLOBAL_COMPONENTS_END_MARK,
			true,
			true,
		);
	}, []);

	/**
	 * !!! DO NOT ADD INDIVIDUAL COMPONENTS TO THIS FILE !!!
	 *
	 * They should be defined as part of the `sharedGlobalComponents` constant, or defined on the route
	 * definition `layout` key.
	 *
	 * @see https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/jira/src/packages/platform/ui/layouts/jira-shared-layout/README.md
	 * @see https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/jira/src/packages/platform/ui/layouts/README.md
	 */
	return (
		<>
			<PerformanceMark metricKey={GLOBAL_COMPONENTS_START_MARK} />
			<JSErrorBoundary
				fallback="unmount"
				id="shared-global-components"
				packageName="page-container"
			>
				<RenderOnClient
				// 💡: "sharedComponents" being rendered last cannot affect SSR, and sharedGlobalComponents are also almost empty
				// 💡: this is why it's important to RenderOnClient them all
				>
					<UFOLabel name="shared-global-components">
						<SharedGlobalComponents sharedGlobalComponents={sharedGlobalComponents} />
					</UFOLabel>
				</RenderOnClient>
			</JSErrorBoundary>
			<JSErrorBoundary fallback="unmount" id="route-global-components" packageName="page-container">
				<UFOLabel name="route-global-components">
					<RouteGlobalComponents />
				</UFOLabel>
			</JSErrorBoundary>
			<PerformanceMark
				metricKey={GLOBAL_COMPONENTS_END_MARK}
				onSet={globalComponentsPerformanceTiming}
			/>
		</>
	);
});
